/**
 * Simple in-memory cache with TTL support
 */

class Cache {
  constructor() {
    this.cache = new Map();
    this.timestamps = new Map();
  }

  /**
   * Set a value in cache with TTL
   * @param {string} key - Cache key
   * @param {any} value - Value to cache
   * @param {number} ttl - Time to live in milliseconds (default 30s)
   */
  set(key, value, ttl = 30000) {
    this.cache.set(key, value);
    this.timestamps.set(key, Date.now() + ttl);
  }

  /**
   * Get a value from cache if not expired
   * @param {string} key - Cache key
   * @returns {any|null} Cached value or null if expired/not found
   */
  get(key) {
    const timestamp = this.timestamps.get(key);
    
    // Check if expired
    if (!timestamp || Date.now() > timestamp) {
      this.delete(key);
      return null;
    }
    
    return this.cache.get(key);
  }

  /**
   * Check if key exists and is not expired
   * @param {string} key - Cache key
   * @returns {boolean} True if key exists and is valid
   */
  has(key) {
    return this.get(key) !== null;
  }

  /**
   * Delete a key from cache
   * @param {string} key - Cache key
   */
  delete(key) {
    this.cache.delete(key);
    this.timestamps.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clear() {
    this.cache.clear();
    this.timestamps.clear();
  }

  /**
   * Get cache statistics
   * @returns {object} Cache stats
   */
  getStats() {
    const now = Date.now();
    const valid = Array.from(this.timestamps.values()).filter(t => t > now).length;
    
    return {
      total: this.cache.size,
      valid,
      expired: this.cache.size - valid
    };
  }
}

// Export singleton instance
export const cache = new Cache();

// Export class for testing
export default Cache;
